import * as React from "react";
import { breakpoints } from "~/utils/breakpoint-utils";
import { ResponsiveObject, Settings } from "react-slick";
import { ThemeColorType } from "~/types/theme-color";
import ImageTextCtaListTile, {
    ImageTextCtaListTileProps
} from "../image-text-cta-list-tile/image-text-cta-list-tile";
import SliderSwiper from "../swiper/slider-swiper";
import { generateColumnImageProps } from "~/utils/image-utils";
import ArrowLink from "../arrow-link/arrow-link";
import ContentSection from "../content-section/content-section";

export interface IContentSliderProps {
    title?: string;
    imageTileTextCtaList?: Array<ImageTextCtaListTileProps>;
    hasNoMarginTop?: boolean;
    hasNoMarginBottom?: boolean;
    bgColor?: ThemeColorType;
    textColor?: ThemeColorType;
    showCtaAsLinkColor?: ThemeColorType;
    viewMoreLink?: string;
}

const swiperBreakpoints = [
    breakpoints.rat,
    breakpoints.rabbit,
    breakpoints.dog,
    breakpoints.lion,
    breakpoints.horse
];

// TODO: Replace this with the old config object
const fittedSymmetricalSigmodialFunction = (x: number) => {
    // figures out the slides to show given a breakpoint using a symmetrical sigmodial function
    const a = 1.1196611;
    const b = 1.969953;
    return 9.540517 + (a - 9.540517) / (1 + Math.pow(x / 5.212277, b));
};

const swiperResponsiveBreakpoints = swiperBreakpoints.map(
    (breakpoint, index): ResponsiveObject => {
        return {
            breakpoint: breakpoint,
            settings: {
                slidesToShow: fittedSymmetricalSigmodialFunction(index)
            }
        };
    }
);

const swiperSettings: Settings = {
    slidesToShow: 4.3,
    swipeToSlide: true,
    lazyLoad: "progressive",
    initialSlide: 0,
    responsive: swiperResponsiveBreakpoints
};

const columnImageProps = generateColumnImageProps(5, true);

const ContentSlider: React.FC<IContentSliderProps> = ({
    title,
    imageTileTextCtaList,
    bgColor,
    textColor,
    showCtaAsLinkColor,
    viewMoreLink,
    ...props
}: IContentSliderProps) => {
    const baseClass = "content-slider";

    const headerTitle = title && (
        <h2 className={`${baseClass}__title h2-style`} dangerouslySetInnerHTML={{ __html: title }} />
    );
    const headerLink = viewMoreLink && <ArrowLink className="product-slider__link" link={viewMoreLink} />;

    return (
        <ContentSection className={baseClass} type="full" {...props}>
            <div className={`${baseClass}__inner`}>
                {headerTitle && (
                    <div className={`${baseClass}__header`}>
                        {headerTitle} {headerLink}
                    </div>
                )}

                <div className={`${baseClass}__items`}>
                    {imageTileTextCtaList && (
                        <SliderSwiper settings={swiperSettings}>
                            {imageTileTextCtaList?.map((tile, index) => (
                                <ImageTextCtaListTile
                                    key={index}
                                    {...tile}
                                    {...columnImageProps}
                                    bgColor={tile.bgColor || bgColor}
                                    textColor={tile.textColor || textColor}
                                    showCtaAsLinkColor={tile.showCtaAsLinkColor || showCtaAsLinkColor}
                                    compact={true}
                                />
                            ))}
                        </SliderSwiper>
                    )}
                </div>
            </div>
        </ContentSection>
    );
};

export default ContentSlider;
